import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import useLoanId from 'hooks/useLoanId';
import { getConditionalArray } from 'utils/data';

import { getDocumentsAction } from 'store/documents/actions';
import {
  getAssetsAction,
  getFinancialInfoPropertiesAction,
  getFinancialInformationSummaryAction,
  getIncomesAction,
  getLiabilitiesAction,
  getLoanAndLockSummaryEditDataAction,
  getLoanContactsViewAction,
  getPropertyAndLoanSummaryEditDataAction,
} from 'store/loanApplications/actions';
import { selectLoanContactsAllowedActions } from 'store/loanApplications/selectors';
import { getDocumentCategoriesAction, getDocumentTypesAction } from 'store/system/actions';

const useFinancialInfoPrefetch = () => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();
  const loanId = useLoanId();
  const { isViewAllowed: isContactsViewAllowed } = useSelector(selectLoanContactsAllowedActions);
  const prefetchedAppIdRef = useRef(null);
  const prefetchedDocsLoanIdRef = useRef(null);
  const [isRecordsLoading, setIsRecordsLoading] = useState(false);

  const prefetchFinancialInfo = useCallback(() => {
    const shouldFetchRecords = prefetchedAppIdRef.current !== applicationId;
    const shouldFetchDocs = loanId && prefetchedDocsLoanIdRef.current !== loanId;

    if (!shouldFetchRecords && !shouldFetchDocs) {
      return;
    }

    const promises = [];

    if (shouldFetchRecords) {
      prefetchedAppIdRef.current = applicationId;
      setIsRecordsLoading(true);

      promises.push(
        dispatch(getAssetsAction({ applicationId })),
        dispatch(getLiabilitiesAction({ applicationId })),
        dispatch(getIncomesAction({ applicationId })),
        dispatch(getFinancialInfoPropertiesAction({ applicationId })),
        dispatch(getFinancialInformationSummaryAction({ applicationId })),
        dispatch(getPropertyAndLoanSummaryEditDataAction({ applicationId })),
        dispatch(getLoanAndLockSummaryEditDataAction({ applicationId })),
        // Contact results: the Contacts tab is the only page that loads them otherwise.
        ...getConditionalArray(isContactsViewAllowed, () => [
          dispatch(getLoanContactsViewAction({ applicationId })),
        ]),
      );
    }

    if (shouldFetchDocs) {
      prefetchedDocsLoanIdRef.current = loanId;

      promises.push(
        dispatch(
          getDocumentsAction({
            loanIdOrLegacyLoanNumber: loanId,
            includeDeleted: false,
            includeFieldsAndResults: false,
          }),
        ),
        dispatch(getDocumentTypesAction()),
        dispatch(getDocumentCategoriesAction()),
      );
    }

    Promise.all(promises).finally(() => setIsRecordsLoading(false));
  }, [applicationId, loanId, isContactsViewAllowed, dispatch]);

  return { prefetchFinancialInfo, isRecordsLoading };
};

export default useFinancialInfoPrefetch;
